import {
  Avatar,
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Edit from "@mui/icons-material/Edit";
import { useEffect, useMemo, useState } from "react";
import WorkOrderCard from "components/QueueBoard/work-order-dashboard-card/WorkOrderDetail";
import { BoardView } from "../types/boardView";

import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { UpdateWorkOrderTech } from "../models/workOrderTypes";
import {
  capitalizeFirstLetter,
  truncateName,
} from "../../common/Utils/commonUtilityFunctions";
import { WorkOrdersByTechnician } from "../models/workOrderTypes";
import LinearProgress from "@mui/material/LinearProgress";
import WorkOrderDialogContainer from "components/workOrder/WorkOrderDialogContainerMain";
import {
  useBoardFilters,
  useBoardLoading,
  useTechBoardData,
} from "../../store/zustandWorkOrderStore";
import { useBoardColumns } from "../shared/hooks/useBoardColumns";
import { useCachedWorkOrderOpen } from "../shared/hooks/useCachedWorkOrderOpen";
import WorkOrderQueueAPI from "../api/workOrderApi";
import TechBoardCustomColumns from "./TechBoardCustomColumns";
import { UnassignedSortDropdown } from "./UnassignedSortDropdown";
import {
  UNASSIGNED_SORT_STORAGE_KEY_PREFIX,
  isUnassignedSortOrder,
  type UnassignedSortOrder,
} from "./unassignedSort.types";
import {
  getColumnWorkOrdersForDrag,
  getSortedUnassignedWorkOrders,
} from "./unassignedSort.utils";
import packageJson from "../../../../package.json";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import { User } from "components/authentication/models/authTypes";
import localStorageHelper from "../../../app/localStorage";
import { ConcurrencyError } from "components/common/Utils/concurrencyError";
import { ConcurrencyDialog } from "components/common/components/ConcurrencyDialog";
import { filterColumnsWithWorkOrders } from "../shared/boardColumnUtils";

const TechBoard = () => {
  const theme = useTheme();
  const loading = useBoardLoading();
  const WorkOrdersByTechnician = useTechBoardData();
  const filters = useBoardFilters();
  const [columns, setColumns] = useBoardColumns<WorkOrdersByTechnician>(
    WorkOrdersByTechnician,
    "techPosition",
  );
  const { showDialog, selectedWorkOrderId, showHideWorkOrderPopup } =
    useCachedWorkOrderOpen({
      loading,
      hasData: (WorkOrdersByTechnician?.length ?? 0) > 0,
    });

  // Get zoom level from filters (set in FilterPanel)
  const zoomLevel = filters?.boardZoom ?? 1.0;
  const visibleColumns = useMemo(
    () => filterColumnsWithWorkOrders(columns),
    [columns],
  );
  const visibleColumnEntries = useMemo(
    () =>
      Object.entries(columns)
        .filter(([, column]) => column.workOrders.length > 0)
        .sort(
          ([, a], [, b]) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
        ),
    [columns],
  );
  const [user] = useState<User | undefined>(
    localStorageHelper.getByKey("user"),
  );
  // Add state to track the recently dropped work order
  const [recentlyDroppedWorkOrder, setRecentlyDroppedWorkOrder] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (recentlyDroppedWorkOrder !== null) {
      const timer = setTimeout(() => {
        setRecentlyDroppedWorkOrder(null);
      }, 3000); // 3 seconds to match the CSS animation duration

      return () => clearTimeout(timer);
    }
  }, [recentlyDroppedWorkOrder]);

  const [showConcurrencyDialog, setShowConcurrencyDialog] = useState(false);
  const [concurrencyMessage, setConcurrencyMessage] = useState("");
  const [userName] = useState<string | "">(
    localStorage.getItem("username") || "",
  );
  const [unassignedSortOrder, setUnassignedSortOrder] =
    useState<UnassignedSortOrder>(() => {
      const saved = localStorage.getItem(
        `${UNASSIGNED_SORT_STORAGE_KEY_PREFIX}${localStorage.getItem("username") || ""}`,
      );
      return isUnassignedSortOrder(saved) ? saved : "position";
    });

  const handleUnassignedSortChange = (order: UnassignedSortOrder) => {
    setUnassignedSortOrder(order);
    localStorage.setItem(
      `${UNASSIGNED_SORT_STORAGE_KEY_PREFIX}${userName}`,
      order,
    );
  };

  const getSortedWorkOrders = (column: WorkOrdersByTechnician) =>
    getSortedUnassignedWorkOrders(column, unassignedSortOrder);

  const getWorkOrdersForDrag = (column: WorkOrdersByTechnician) =>
    getColumnWorkOrdersForDrag(column, unassignedSortOrder);

  const onDragEnd = async (
    result: DropResult,
    columns: WorkOrdersByTechnician[],
    setColumns: React.Dispatch<React.SetStateAction<WorkOrdersByTechnician[]>>,
  ) => {
    if (!result.destination) return;
    const { source, destination } = result;

    if (source.droppableId !== destination.droppableId) {
      const sourceColumn = columns[source.droppableId];
      const destColumn = columns[destination.droppableId];
      const sourceItems = [...getWorkOrdersForDrag(sourceColumn)];
      const destItems = [...getWorkOrdersForDrag(destColumn)];
      const [removed] = sourceItems.splice(source.index, 1);
      const updatedRemoved = {
        ...removed,
        technicianIdentity: destColumn.identity,
      };
      destItems.splice(destination.index, 0, updatedRemoved);

      const updatedSourceItems = sourceItems.map((item, index) => ({
        ...item,
        position: index,
        techPosition: index,
      }));
      const updatedDestItems = destItems.map((item, index) => ({
        ...item,
        position: index,
        techPosition: index,
      }));

      setColumns({
        ...columns,
        [source.droppableId]: {
          ...sourceColumn,
          workOrders: updatedSourceItems,
        },
        [destination.droppableId]: {
          ...destColumn,
          workOrders: updatedDestItems,
        },
      });

      // Set the recently dropped work order ID for flashing effect AFTER columns are updated
      const droppedWorkOrderId = parseInt(result.draggableId);
      //console.log("Setting recently dropped work order:", droppedWorkOrderId);
      setRecentlyDroppedWorkOrder(droppedWorkOrderId);

      const updatedWO: UpdateWorkOrderTech = {
        invoiceDocumentIdentity: updatedRemoved.invoiceDocumentIdentity,
        technicianIdentity: updatedRemoved.technicianIdentity,
        modifiedDate: new Date().toISOString(),
      };

      try {
        await WorkOrderQueueAPI.UpdateWorkOrderTechnician(updatedWO);
      } catch (error) {
        if (error instanceof ConcurrencyError) {
          setConcurrencyMessage(
            "The work order has been updated by some other user, please refresh the board and try again!",
          );
          setShowConcurrencyDialog(true);
          return;
        }
        throw error;
      }

      const updatedPositions = {
        sourceItems: updatedSourceItems.map((item, index) => ({
          invoiceDocumentIdentity: item.invoiceDocumentIdentity,
          position: index,
        })),
        destItems: updatedDestItems.map((item, index) => ({
          invoiceDocumentIdentity: item.invoiceDocumentIdentity,
          position: index,
        })),
      };
      //Update Positions in the database
      await WorkOrderQueueAPI.updateWorkOrdersTechPositions(updatedPositions);
    } else {
      const column = columns[source.droppableId];
      const copiedItems = [...getWorkOrdersForDrag(column)];
      const [removed] = copiedItems.splice(source.index, 1);
      copiedItems.splice(destination.index, 0, removed);
      const updatedCopiedItemsWithIndices = copiedItems.map(
        (workOrder, index) => {
          return {
            ...workOrder,
            position: index,
            techPosition: index,
          };
        },
      );

      setColumns((prevColumns: WorkOrdersByTechnician[]) => ({
        ...prevColumns,
        [source.droppableId]: {
          ...column,
          workOrders: updatedCopiedItemsWithIndices,
        },
      }));

      // Set the recently dropped work order ID for flashing effect AFTER columns are updated
      const droppedWorkOrderId = parseInt(result.draggableId);
      // console.log(
      //   "Setting recently dropped work order (same column):",
      //   droppedWorkOrderId
      // );
      setRecentlyDroppedWorkOrder(droppedWorkOrderId);

      const listToUpdate = {
        items: copiedItems.map((item, index) => ({
          invoiceDocumentIdentity: item.invoiceDocumentIdentity,
          position: index,
        })),
      };

      await WorkOrderQueueAPI.updateWorkOrdersTechPositions(listToUpdate);
    }

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "onDragEndTechBoard",
      {
        elapsed_ms: "124",
        title: "Wo Technician Board",
        componentName: "technician-board.tsx",
        version: packageJson.version,
      },
    );
  };
  const addNewWorkOrder = (techIdentity: number) => {
    //
    localStorage.setItem(`${"IsTechView"}_${userName}`, "true");
    localStorage.setItem(`${"IsStatusView"}_${userName}`, "");
    localStorage.setItem("techIdentity", techIdentity.toString());
    showHideWorkOrderPopup(true);

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "addNewWorkOrderTechBoard",
      {
        elapsed_ms: "124",
        title: "Wo Technician Board",
        componentName: "technician-board.tsx",
        version: packageJson.version,
      },
    );
  };

  const showHideWorkOrderPopupHandler = (isPopupToShow: boolean) => {
    showHideWorkOrderPopup(isPopupToShow);
    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      (isPopupToShow ? "open" : "close") + "NewWorkOrderTechBoard",
      {
        elapsed_ms: "124",
        title: "Wo Technician Board",
        componentName: "technician-board.tsx",
        version: packageJson.version,
      },
    );
  };

  const [showConfig, setShowConfig] = useState(false);
  const handleSaveColumnOrder = (updatedColumns: WorkOrdersByTechnician[]) => {
    setColumns(updatedColumns);
    setShowConfig(false);
  };

  return (
    <Box
      sx={{
        overflow: "auto",
        height: "100%",
        backgroundColor: theme.palette.mode === "light"
          ? "#f5f5f5"
          : "#1e1e1e",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          minHeight: "100%",
          padding: 2,
        }}
      >
        {showConfig ? (
          <TechBoardCustomColumns
            columns={columns}
            onSave={handleSaveColumnOrder}
            onCancel={() => setShowConfig(false)}
          />
        ) : (
          <>
            {loading ? (
              <LinearProgress />
            ) : (
              <DragDropContext
                key={"worker"}
                onDragEnd={(result) => {
                  onDragEnd(result, columns, setColumns);
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: zoomLevel,
                    width: "100%",
                    minHeight: "100%",
                    alignItems: "stretch",
                  }}
                >
                  {visibleColumnEntries.map(([columnId, column], index) => {
                      return (
                        <Droppable key={columnId} droppableId={columnId}>
                          {(provided, snapshot) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              key={columnId}
                              sx={{
                                flexGrow: 1, //Object.keys(columns).length > 5 ? 1 : 0,
                                flexBasis:
                                  visibleColumns.length > 4
                                    ? "0"
                                    : "auto", // Ensures columns distribute evenly
                                flexShrink: 0, // Prevents overflow
                                minWidth:
                                  visibleColumns.length > 4
                                    ? `${300 * zoomLevel}px`
                                    : "0px", // Removes the fixed 23% width
                                maxWidth: `${300 * zoomLevel}px`,
                                transition: "all 0.2s ease-in-out",
                              }}
                            >
                              <Card
                                elevation={0}
                                sx={{
                                  borderRadius: 2,
                                  bgcolor:
                                    theme.palette.mode === "light"
                                      ? "#ebeced"
                                      : "#2d2d2d",
                                  overflow: "visible",
                                  height: "100%",
                                }}
                              >
                                <CardContent
                                  className="tech"
                                  sx={{
                                    padding: `${12 * zoomLevel}px`,
                                    textAlign: "right",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 2,
                                    backgroundColor:
                                      theme.palette.mode === "light"
                                        ? "#ebeced"
                                        : "#2d2d2d",
                                    borderRadius: "8px 8px 0 0",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                                    "&:last-child": {
                                      paddingBottom: `${12 * zoomLevel}px`,
                                    },
                                  }}
                                >
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Avatar
                                      sx={{
                                        height: `${24 * zoomLevel}px`,
                                        width: `${24 * zoomLevel}px`,
                                        bgcolor: theme.palette.primary.main,
                                        fontSize: `${12 * zoomLevel}px`,
                                        fontWeight: 600,
                                      }}
                                    >
                                      {capitalizeFirstLetter(
                                        column.displayText.charAt(0),
                                      )}
                                    </Avatar>
                                    <Typography
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: `${14 * zoomLevel}px`,
                                        color: theme.palette.text.primary,
                                        letterSpacing: "0.01em",
                                      }}
                                    >
                                      {truncateName(column.displayText, 25)}
                                    </Typography>

                                    <Box flexGrow={1} />

                                    {column.identity === 0 && (
                                      <UnassignedSortDropdown
                                        value={unassignedSortOrder}
                                        onChange={handleUnassignedSortChange}
                                        zoomLevel={zoomLevel}
                                      />
                                    )}

                                    <Tooltip title="Customize the column order">
                                      <IconButton
                                        onClick={() => setShowConfig(true)}
                                        sx={{
                                          marginLeft: `${1 * zoomLevel}px`,
                                          padding: `${2 * zoomLevel}px`,
                                          width: `${28 * zoomLevel}px`,
                                          height: `${28 * zoomLevel}px`,
                                          color: "#2371E7",
                                          bgcolor: "#ffffff",
                                          borderRadius: `${3 * zoomLevel}px`,
                                          "&:hover": {
                                            bgcolor: theme.palette.mode === "light"
                                              ? "rgba(0, 0, 0, 0.04)"
                                              : "rgba(255, 255, 255, 0.08)",
                                          },
                                        }}
                                        size="small"
                                      >
                                        <Edit
                                          sx={{
                                            width: `${16 * zoomLevel}px`,
                                            height: `${16 * zoomLevel}px`,
                                            fontSize: `${15 * zoomLevel}px`,
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Add new work order">
                                      <IconButton
                                        onClick={() =>
                                          addNewWorkOrder(column.identity)
                                        }
                                        sx={{
                                          marginLeft: `${-5.3 * zoomLevel}px`,
                                          padding: `${2 * zoomLevel}px`,
                                          width: `${28 * zoomLevel}px`,
                                          height: `${28 * zoomLevel}px`,
                                          color: "#2371E7",
                                          bgcolor: "#ffffff",
                                          borderRadius: `${3 * zoomLevel}px`,
                                          "&:hover": {
                                            bgcolor: theme.palette.mode === "light"
                                              ? "rgba(0, 0, 0, 0.04)"
                                              : "rgba(255, 255, 255, 0.08)",
                                          },
                                        }}
                                        size="small"
                                      >
                                        <AddIcon
                                          sx={{
                                            width: `${16 * zoomLevel}px`,
                                            height: `${16 * zoomLevel}px`,
                                            fontSize: `${15 * zoomLevel}px`,
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>

                                  {/* Hours Summary Badge */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: 2,
                                      backgroundColor: theme.palette.mode === "light"
                                        ? "#fafafa"
                                        : "rgba(255, 255, 255, 0.05)",
                                      borderRadius: 1,
                                      padding: `${6 * zoomLevel}px ${12 * zoomLevel}px`,
                                      mt: `${8 * zoomLevel}px`,
                                      border: theme.palette.mode === "light"
                                        ? "1px solid #eeeeee"
                                        : "1px solid rgba(255, 255, 255, 0.1)",
                                      boxShadow: theme.palette.mode === "light"
                                        ? "0 2px 8px rgba(0, 0, 0, 0.06)"
                                        : "0 2px 8px rgba(0, 0, 0, 0.2)",
                                    }}
                                  >
                                    <Box sx={{ textAlign: "center" }}>
                                      <Typography
                                        component="span"
                                        sx={{
                                          fontSize: `${10 * zoomLevel}px`,
                                          color: theme.palette.text.secondary,
                                          mr: 0.5,
                                        }}
                                      >
                                        Quoted:
                                      </Typography>
                                      <Typography
                                        component="span"
                                        sx={{
                                          fontSize: `${11 * zoomLevel}px`,
                                          fontWeight: 700,
                                          color: theme.palette.text.primary,
                                        }}
                                      >
                                        {column.hoursQuoted?.toFixed(2) ?? "0.00"} Hrs
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        width: "1px",
                                        height: `${14 * zoomLevel}px`,
                                        backgroundColor: theme.palette.divider,
                                      }}
                                    />
                                    <Box sx={{ textAlign: "center" }}>
                                      <Typography
                                        component="span"
                                        sx={{
                                          fontSize: `${10 * zoomLevel}px`,
                                          color: theme.palette.text.secondary,
                                          mr: 0.5,
                                        }}
                                      >
                                        Clocked:
                                      </Typography>
                                      <Typography
                                        component="span"
                                        sx={{
                                          fontSize: `${11 * zoomLevel}px`,
                                          fontWeight: 700,
                                          color: theme.palette.text.primary,
                                        }}
                                      >
                                        {column.hoursClocked?.toFixed(2) ?? "0.00"} Hrs
                                      </Typography>
                                    </Box>
                                  </Box>
                                </CardContent>

                                {getSortedWorkOrders(column).map((workOrder, idx) => (
                                  <Draggable
                                    key={workOrder.invoiceDocumentIdentity}
                                    draggableId={String(
                                      workOrder.invoiceDocumentIdentity,
                                    )}
                                    index={idx}
                                  >
                                    {(provided, snapshot) => (
                                      <Box
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        ml={zoomLevel}
                                        mr={zoomLevel}
                                        p={0.5 * zoomLevel}
                                        sx={{
                                          cursor: snapshot.isDragging
                                            ? "grabbing"
                                            : "grab",
                                        }}
                                      >
                                        <WorkOrderCard
                                          index={idx}
                                          color=""
                                          boardView={BoardView.Technician}
                                          workOrderObj={workOrder}
                                          recentlyDroppedWorkOrder={
                                            recentlyDroppedWorkOrder ||
                                            undefined
                                          }
                                          isCollapsed={
                                            filters?.collapseWorkOrders ?? false
                                          }
                                        />
                                      </Box>
                                    )}
                                  </Draggable>
                                ))}
                              </Card>
                              {provided.placeholder}
                            </Box>
                          )}
                        </Droppable>
                      );
                    })}
                </Box>
              </DragDropContext>
            )}
            {showDialog && (
              <WorkOrderDialogContainer
                showHideWoPopup={showHideWorkOrderPopupHandler}
                InvoiceDocIdentity={selectedWorkOrderId || undefined}
                open={true}
                cacheNewWorkOrderIdOnSave
              />
            )}

            {/* Concurrency Conflict Dialog */}
            <ConcurrencyDialog
              open={showConcurrencyDialog}
              onDismiss={() => {
                setShowConcurrencyDialog(false);
              }}
              onRefresh={() => {
                setShowConcurrencyDialog(false);
                window.dispatchEvent(new CustomEvent("refreshWorkOrders"));
              }}
              message={concurrencyMessage}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default TechBoard;
